import './DashboardCard.scss';
import cn from 'classnames';
import {
  CardSize,
  ChartConfig,
  ChartDateRange,
  ChartId,
  DashboardId,
  DimensionId,
  resolveComparison,
  resolveDateRangeControl
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import {
  ComponentProps,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { Form, Overlay, OverlayTrigger, Popover } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdDeleteOutline, MdDragIndicator, MdLink, MdLinkOff, MdOpenInFull } from 'react-icons/md';
import { ChartActionsMenu, ChartContextInfo, ChartFilterLine } from '@Components/ChartRenderer';
import { ChartContainer } from '@Components/ChartContainer';
import { IconButton } from '@Components/IconButton/IconButton';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { DatePicker } from '@Components/DatePicker';
import { DatePickerButton } from '@Components/DatePicker/DatePickerButton/DatePickerButton';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Design, Intent, Placement, Size } from '@Components/designConstants';
import { useSetAreYouSure } from '@src/redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useDebouncedCallback } from '../../utils/hooks/useDebouncedCallback';
import { useElementWidth } from '../../utils/hooks/useElementWidth';
import { TruncationAxis, useIsTruncated } from '../../utils/hooks/useIsTruncated';
import { useChartConfig, useChartEnvelope } from '../Charts/hooks/useChartData';
import { FetchError } from '../../utils/fetch-error';
import { useWorkingCopy } from '../Charts/hooks/useWorkingCopy';
import { ChartCard } from './cards/ChartCard';
import { DashboardCardEditControls } from './DashboardCardEditControls';
import { CardChartProvider, useThisCard, useThisCardChartData } from './CardChartContext';
import { useDashboardView } from './DashboardViewContext';
import { toChartDateRange, toPickerRange } from './date-range';
import { buildDashboardFilterChips } from './dashboard-filter-utils';
import { DashboardCard as DashboardCardData } from './dashboard-types';
import { dashboardMutationKey } from './hooks/useDashboard';
import { toFocusedViewSearch } from './dashboard-view-params';
import {
  getCardDimensions,
  pinnedDateAriaLabel,
  shouldCollapseCardDatePicker,
  toGridPlacement
} from './dashboard-utils';
import { useDragActions } from './drag/DragContext';

// Every card is a ChartConfig-by-reference now: resolve its ChartId and render through the shared
// ChartRenderer (graphic/table/stat all dispatch there). No chart id => an empty placeholder. An
// unsaved working copy (the agent's pending edit) renders as a PREVIEW over the saved chart;
// ChartCard reads both from the card's CardChartProvider.
function renderCardContent(
  card: DashboardCardData,
  onRemove: (() => void) | undefined,
  removeDisabled: boolean
) {
  if (!card.config?.chartId) return <div className='dashboard-card-placeholder'>{card.type}</div>;
  return <ChartCard onRemove={onRemove} removeDisabled={removeDisabled} />;
}

interface DashboardCardProps {
  card: DashboardCardData;
  // This card's index in the dashboard's `cards` array, which is 1:1 with `layout.placements` on
  // disk. "Save as a new chart -> Show it here" repoints by index rather than by position, because
  // `card.position` is whatever compaction produced on read and need not match what is stored.
  cardIndex: number;
  dashboardId: DashboardId;
  isEditMode: boolean;
  // Whether this user may change the DASHBOARD. Passed as a prop beside `isEditMode` because the
  // two travel together and the builder already threads that one to every card.
  //
  // It is not the same question as edit mode. Edit mode governs drag, rename and the per-card ✕;
  // this governs the TWO removal paths that draw in VIEW mode as well, each of which saves the
  // dashboard on its own.
  canEdit: boolean;
  children?: ReactNode;
  // Drop this card's placement from the dashboard. Called only after the user confirms (see
  // confirmRemove below); when it persists is the builder's call, not the card's.
  onRemove: (id: string) => void;
  // Switch this card to another preset size. Like onRemove, the builder owns the reflow and when
  // it persists; the card only reports the choice.
  onChangeSize: (id: string, size: CardSize) => void;
  // The saved card's opt-out, for links that leave the dashboard: `card` can hold a pending one.
  savedIgnoreDashboardFilters?: DimensionId[];
}

export const DashboardCard = ({
  card,
  cardIndex,
  dashboardId,
  isEditMode,
  canEdit,
  children,
  onRemove,
  onChangeSize,
  savedIgnoreDashboardFilters
}: DashboardCardProps) => {
  const { startDrag } = useDragActions();
  const navigate = useNavigate();
  const { search: dashboardSearch } = useLocation();
  const { cid: company } = useCompanyInfo();
  // A removal is saved against the saved config; sent during another save, it could land second
  // and write that save's changes back.
  const isSavingDashboard =
    useIsMutating({ mutationKey: dashboardMutationKey(company, dashboardId) }) > 0;
  const {
    perChartRange,
    setPerChartRange,
    resolveRange,
    resolveComparisonRange,
    resolveKpi,
    resolveMetric,
    resolveBreakdown,
    dashboardFiltersWithoutPreview
  } = useDashboardView();
  const cardRef = useRef<HTMLDivElement>(null);
  // Title measurements the date picker reads to decide whether it has room to spell out its range
  // (see shouldCollapseCardDatePicker): the room the title has, and what it would need for one line.
  const [titleSlotRef, titleSlotWidth] = useElementWidth<HTMLDivElement>();
  const [titleNaturalRef, titleWidth] = useElementWidth<HTMLSpanElement>();
  const dims = getCardDimensions(card);
  const placement = toGridPlacement(card.position, dims);
  const chartId = card.config?.chartId;

  // The agent's pending edit to THIS card, if any. When present the card renders it as a preview and
  // reveals the exits (Save ▾ / Discard) under the title.
  const { workingCopy, setWorkingCopy, clearWorkingCopy } = useWorkingCopy(
    company,
    chartId ?? null
  );
  const previewConfig = workingCopy ?? undefined;

  // The chart's title, for the removal confirm, plus whether the server will serve this chart at
  // all. Reads the same cache entry the card's date picker and the agent's focus context already
  // populate (identical key), so both answers cost no request.
  //
  // This query, not the render query, is what knows about access: `useChartData` skips /getChart
  // whenever a working copy is present, so it would never see a 403 on a previewed card.
  const { data: envelope, error: accessError } = useChartEnvelope(company, chartId ?? null);
  const savedConfig = envelope?.config;
  const isChartRestricted = accessError instanceof FetchError && accessError.status === 403;
  const setAreYouSure = useSetAreYouSure();

  // Fed by ChartLoadingTracker below (a sibling of ChartContainer, not one of its children — see its
  // comment for why). Reflects a true first-load only: a background breakdown refetch keeps the
  // previous chart on screen and never flips this back to true.
  const [isChartLoading, setIsChartLoading] = useState(false);
  // A working copy for a chart this user may no longer see would otherwise keep rendering — the
  // preview path never asks the server. Drop it, which removes the preview, the Update/Discard
  // controls that appear beside it, and the stale config the agent's focus context would read.
  useEffect(() => {
    if (isChartRestricted && workingCopy) {
      clearWorkingCopy();
    }
  }, [isChartRestricted, workingCopy, clearWorkingCopy]);

  // Removal always goes through a confirm, on both surfaces. The card is the only handle on a
  // placement and neither surface has an undo (the builder's undo/redo is still unwired), so a stray
  // click would otherwise silently drop a chart. The message spells out what is NOT happening: charts
  // are shared BY REFERENCE, so "remove" here reads as "delete this chart" unless we say otherwise.
  const confirmRemove = () => {
    const title = previewConfig?.title ?? savedConfig?.title;
    setAreYouSure({
      title: 'Remove chart from dashboard?',
      intent: Intent.WARNING,
      message: (
        <>
          {title ? <strong>{title}</strong> : 'This chart'} will be taken off this dashboard. The
          chart itself stays in the chart library, and any other dashboard using it is unaffected.
        </>
      ),
      okayText: 'Remove',
      onOkay: () => onRemove(card.id)
    });
  };

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isEditMode || !cardRef.current) return;
      e.preventDefault();
      const label = previewConfig?.title ?? savedConfig?.title ?? 'Chart';
      startDrag(card, label, e.clientX, e.clientY, cardRef.current);
    },
    [isEditMode, startDrag, card, previewConfig?.title, savedConfig?.title]
  );

  const dragHandle = isEditMode ? (
    <InteractiveSpan className='dashboard-card-drag-handle' onPointerDown={handlePointerDown}>
      <MdDragIndicator />
    </InteractiveSpan>
  ) : null;

  // The card header owns the chart title (top-left). ChartCard renders the same chart with
  // showTitle={false}, so the body draws no title — one title, not two. Bundled into one div (rather
  // than split across ChartContainer's title/leftActions slots) because those slots stack vertically —
  // this keeps the drag handle and title on one row, same as before the ChartContainer swap.
  const title = (
    <div ref={titleSlotRef} className='dashboard-card-header-content'>
      {dragHandle}
      {chartId && (
        <DashboardCardTitle
          naturalWidthRef={titleNaturalRef}
          isEditMode={isEditMode}
          savedTitle={savedConfig?.title}
          setWorkingCopy={setWorkingCopy}
        />
      )}
    </div>
  );

  // Edit mode's own actions row: remove-from-dashboard and the ⋮ menu. A pending edit's exits sit
  // on their own row below (see `editExits`), in this mode as in view mode.
  const editActions =
    isEditMode && chartId ? (
      <div className='dashboard-card-edit-actions'>
        <IconButton
          aria-label='Remove from dashboard'
          tooltip={{ text: 'Remove from dashboard' }}
          design={Design.NEUTRAL}
          size={Size.MD}
          onClick={confirmRemove}
        >
          <MdDeleteOutline />
        </IconButton>
        {/* Resizing is a LAYOUT edit, so it has to be reachable from the mode layout editing happens
            in — the ⋮ menu is otherwise view-mode only and the picker was unreachable here. No
            `onRemove`: the ✕ above already owns removal in this mode, and passing it would draw the
            row twice. */}
        <CardActionsMenu
          cardSize={card.size}
          onChangeSize={canEdit ? (next: CardSize) => onChangeSize(card.id, next) : undefined}
        />
      </div>
    ) : null;

  // View mode: a per-chart date picker showing the EFFECTIVE range (this chart's own override if
  // set, else the dashboard's global range); a link toggle; and a quiet button to open this chart
  // full-size. While LINKED to the global date the picker is disabled — click the link button to
  // break the chart away (seeding its own range with whatever it currently shows, so nothing jumps),
  // then the picker is editable. Click again to re-follow the global.
  //
  // TWO ways a card stops following the dashboard date, and the toggle means different things in
  // each. `hasOwnRange` is the viewer having broken this card away with the toggle. `followsPicker`
  // is the CHART's own declaration: no `dateRange` control means it never participates in the
  // dashboard's date at all (see applyDateRangeOverrides), so it is independent from the moment it
  // loads — which is what the link icon and the picker's contents have to reflect.
  const { data: chartConfig } = useChartConfig(company, chartId ?? null);
  const hasOwnRange = Boolean(chartId && perChartRange[chartId] !== undefined);
  const followsPicker = Boolean(chartConfig && resolveDateRangeControl(chartConfig));
  const isIndependent = hasOwnRange || !followsPicker;

  // The focused view opens on this card's dates, view picks and saved-or-linked filters, never an
  // unsaved agent change. Anything the user never touched is omitted, so the chart keeps its default.
  const focusedViewSearch = () =>
    chartId
      ? toFocusedViewSearch(
          {
            dateRange: resolveRange(chartId),
            comparisonRange: resolveComparisonRange(chartId),
            kpiId: resolveKpi(chartId),
            metric: resolveMetric(chartId),
            breakdown: resolveBreakdown(chartId)
          },
          {
            dashboardFilters: dashboardFiltersWithoutPreview,
            ignoreDashboardFilters: savedIgnoreDashboardFilters
          }
        )
      : '';
  // A restricted card gets NO action row. RestrictedCard replaces only ChartContainer's children,
  // so without this the card would still offer a date picker, a follow toggle and "Open in focused
  // view" — and that last one navigates to a page that answers 403 in turn.
  const pinnedDateLabel = pinnedDateAriaLabel({ hasOwnRange, followsPicker });
  const viewActions =
    !isEditMode && chartId && !isChartRestricted ? (
      <div className='dashboard-card-view-actions'>
        <CardDatePicker
          chartId={chartId}
          isIndependent={isIndependent}
          followsPicker={followsPicker}
          titleWidth={titleWidth}
          titleSlotWidth={titleSlotWidth}
        />
        {/* The same wording as the aria-label, shown on hover so the icon explains itself. Wrapped in a
            span because a disabled button fires no hover events, and the disabled state (a chart
            pinning its own dates) is the one that most needs explaining. */}
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement={Placement.BOTTOM}
          overlay={
            <Popover className='info-tooltip-popover chart-tooltip'>
              <Popover.Body>{pinnedDateLabel}</Popover.Body>
            </Popover>
          }
        >
          <span className='dashboard-card-date-link'>
            <IconButton
              aria-label={pinnedDateLabel}
              // Nothing to toggle on a chart that pins its own dates and has no override yet: it
              // cannot be made to follow the dashboard, so the button would be a promise we break.
              disabled={!followsPicker && !hasOwnRange}
              onClick={
                hasOwnRange
                  ? () => setPerChartRange(chartId, undefined)
                  : () =>
                      setPerChartRange(
                        chartId,
                        toChartDateRange(toPickerRange(resolveRange(chartId)))
                      )
              }
              design={Design.NEUTRAL}
              size={Size.MD}
            >
              {isIndependent ? <MdLinkOff /> : <MdLink />}
            </IconButton>
          </span>
        </OverlayTrigger>
        <IconButton
          aria-label='Open in focused view'
          // Carries where we came from (not the URL — this is navigation context, not a
          // shareable/bookmarkable property of the chart) so SingleChartView can offer a way back to
          // THIS dashboard instead of only the dashboards list.
          onClick={() =>
            navigate(`/${company}/charts/${chartId}${focusedViewSearch()}`, {
              state: { fromDashboardId: dashboardId, fromDashboardSearch: dashboardSearch }
            })
          }
          design={Design.NEUTRAL}
          size={Size.MD}
        >
          <MdOpenInFull />
        </IconButton>
        {/* The ⋮ menu includes "Remove from dashboard" only when a caller passes onRemove, so
            withholding it IS the gate — no new prop and no disabled row. This covers a chart the
            user CAN see on a dashboard they cannot edit; a RESTRICTED chart never gets this far,
            because the whole action row above is already gated on `!isChartRestricted`. (Don't
            reach for "the menu renders nothing without data" instead: CardActionsMenu passes a
            previewConfig, and useChartData skips /getChart whenever it has one, so a restricted
            chart with a working copy does have data.) */}
        <CardActionsMenu
          cardSize={card.size}
          onChangeSize={canEdit ? (next: CardSize) => onChangeSize(card.id, next) : undefined}
          onRemove={canEdit ? confirmRemove : undefined}
          removeDisabled={isSavingDashboard}
        />
      </div>
    ) : null;

  // The Save ▾ / Discard exits for a pending working copy (e.g. a chart title just edited, or an
  // agent edit), in either mode — a pending edit is actionable right away. They stack under the
  // title (ChartContainer's leftActions, in the same column as the title) rather than taking a place
  // in the action row: with an "Unsaved changes" label and two text buttons they're far wider than
  // the icons, and on a one-third card they would squeeze the title down to nothing. Sharing the
  // title's column means a wrapped title pushes them down instead of overlapping them (see
  // DashboardCard.scss). Withheld on a restricted card in view mode, like the rest of its actions.
  const editExits =
    chartId && workingCopy && (isEditMode || !isChartRestricted) ? (
      <DashboardCardEditControls
        company={company}
        dashboardId={dashboardId}
        chartId={chartId}
        cardIndex={cardIndex}
        workingCopy={workingCopy}
        onClear={clearWorkingCopy}
        canEditDashboard={canEdit}
      />
    ) : undefined;

  // The chart's filters, as a small line directly under the title — above the exits, in the same
  // leftActions column. Withheld from a restricted card, like the rest of its chrome.
  const filterLine = chartId && !isChartRestricted ? <CardFilterLine /> : undefined;

  return (
    <CardChartScope
      chartId={chartId}
      previewConfig={previewConfig}
      ignoreDashboardFilters={card.config?.ignoreDashboardFilters}
    >
      <div
        ref={cardRef}
        data-card-id={card.id}
        className={cn('dashboard-card', { 'edit-mode': isEditMode })}
        style={placement}
      >
        {chartId && <ChartLoadingTracker onLoadingChange={setIsChartLoading} />}
        <ChartContainer
          title={title}
          rightActions={isEditMode ? editActions : viewActions}
          leftActions={
            <>
              {filterLine}
              {editExits}
            </>
          }
          viewRightActions
          isLoading={isChartLoading}
          className='dashboard-card-chart-container'
        >
          {/* THE SECOND REMOVAL PATH. ChartCard's own "Remove from dashboard" button draws on a
              404 in VIEW mode, independently of edit mode, and removal saves the dashboard
              straight through. Hiding *Edit Dashboard* does not cover it — withholding the
              callback does. */}
          {children ??
            renderCardContent(card, canEdit ? confirmRemove : undefined, isSavingDashboard)}
        </ChartContainer>
      </div>
    </CardChartScope>
  );
};

interface CardChartScopeProps extends Partial<
  Omit<ComponentProps<typeof CardChartProvider>, 'children'>
> {
  children: ReactElement;
}

// A card with no chart has no render query to share, so it gets no provider.
const CardChartScope = ({ chartId, children, ...card }: CardChartScopeProps) => {
  if (!chartId) return children;
  return (
    <CardChartProvider chartId={chartId} {...card}>
      {children}
    </CardChartProvider>
  );
};

interface ChartLoadingTrackerProps {
  onLoadingChange: (isLoading: boolean) => void;
}

// Tracks the same query ChartCard renders from (useThisCardChartData — dedupes via the query
// cache, no extra fetch) so DashboardCard can drive ChartContainer's own loading spinner.
//
// Rendered as a SIBLING of ChartContainer, never as one of its children: ChartContainer swaps its
// children out for a spinner while isLoading is true, which would unmount whatever's reporting that
// same isLoading — the one thing that could ever flip it back to false once data arrives. Learned
// this the hard way: ChartCard used to report its own loading state this way and it deadlocked
// (spinner stuck forever, since nothing was left mounted to notice the fetch had finished). This
// tracker is never hidden, so it can't get caught in that trap.
const ChartLoadingTracker = ({ onLoadingChange }: ChartLoadingTrackerProps) => {
  const { isLoading, isError } = useThisCardChartData();

  // A failed chart is never "still loading". Reporting the two together means the container can't
  // sit on a spinner over a query that has already given up — the card gets to show what went
  // wrong, which is the whole point of having a failure state.
  const isLoadingAndNotFailed = isLoading && !isError;

  useEffect(() => {
    onLoadingChange(isLoadingAndNotFailed);
  }, [isLoadingAndNotFailed, onLoadingChange]);

  return null;
};

interface CardActionsMenuProps {
  cardSize: CardSize;
  // Absent when this user may not edit the dashboard. ChartActionsMenu already includes the
  // "Remove from dashboard" and size rows only when it receives the matching handler, so
  // withholding one removes those rows — no disabled state, and no second gate beside the one the
  // menu already has.
  onChangeSize?: (size: CardSize) => void;
  onRemove?: () => void;
  removeDisabled?: boolean;
}

// The card's overflow (⋮) menu. Reads the chart through the card's shared render query
// (useThisCardChartData), so React Query serves it from the entry the header already populated (no
// extra network) and an export always matches what's drawn, including a pending edit's preview.
// Renders nothing until the chart loads.
//
// This is where removal lives in VIEW mode: an infrequent, destructive action belongs behind the ⋮
// rather than as a sixth icon in a row the user clicks through constantly.
const CardActionsMenu = ({
  cardSize,
  onChangeSize,
  onRemove,
  removeDisabled
}: CardActionsMenuProps) => {
  const { data } = useThisCardChartData();

  if (!data) return null;

  return (
    <ChartActionsMenu
      config={data.config}
      data={data.data}
      cardSize={cardSize}
      onChangeCardSize={onChangeSize}
      onRemoveFromDashboard={onRemove}
      removeDisabled={removeDisabled}
    />
  );
};

// Reads the card's shared render query (useThisCardChartData), so it costs no request. The chips
// come from the filters SENT with the data on screen, which can lag the dashboard's current ones.
const CardFilterLine = () => {
  const { resolveBreakdown } = useDashboardView();
  const { chartId, ignoreDashboardFilters } = useThisCard();
  const { data } = useThisCardChartData();

  if (!data) return null;

  return (
    <ChartFilterLine
      config={data.config}
      data={data.data}
      breakdown={resolveBreakdown(chartId)}
      dashboardFilterChips={buildDashboardFilterChips(data, ignoreDashboardFilters)}
    />
  );
};

interface CardDatePickerProps {
  chartId: ChartId;
  isIndependent: boolean;
  followsPicker: boolean;
  titleWidth?: number;
  titleSlotWidth?: number;
}

// The per-chart date picker. For a period-over-period chart (two date ranges) it also exposes the
// comparison window, seeded from the chart's saved comparison period until the user overrides it.
// Disabled only while the card is actually FOLLOWING the dashboard date (the link toggle in the
// card header controls that). A chart that pins its own dates is independent from load, so its
// picker is editable immediately — and what it edits reaches the data, because a per-card range is
// explicit (see `dateRangeIsExplicit`) where the dashboard's global range is not.
const CardDatePicker = ({
  chartId,
  isIndependent,
  followsPicker,
  titleWidth,
  titleSlotWidth
}: CardDatePickerProps) => {
  const { cid: company } = useCompanyInfo();
  const {
    resolveRange,
    perChartRange,
    setPerChartRange,
    resolveComparisonRange,
    setPerChartComparisonRange
  } = useDashboardView();
  const { data: config } = useChartConfig(company, chartId);

  // `resolveRange` falls back to the DASHBOARD's global range, which is the wrong thing to show on
  // a chart that ignores it — the picker would advertise a window the chart isn't drawing. Fall
  // back to the chart's own saved base window instead, mirroring what the comparison half below
  // has always done with q1. An explicit per-card range still wins over both.
  const savedBase: ChartDateRange | undefined = config?.queries[0]?.dateRange;
  const effectiveRange =
    perChartRange[chartId] ?? (followsPicker ? resolveRange(chartId) : savedBase);
  const isComparison = Boolean(config && resolveComparison(config));
  const [pickerRef, pickerWidth] = useElementWidth<HTMLSpanElement>();
  const [expandedPickerRef, expandedPickerWidth] = useElementWidth<HTMLSpanElement>();

  const commonProps = {
    range: toPickerRange(effectiveRange),
    onChange: (r: DateRange) => setPerChartRange(chartId, toChartDateRange(r)),
    datePresets: true,
    design: Design.NEUTRAL,
    size: Size.MD,
    disabled: !isIndependent,
    // While the card is LINKED to the dashboard's own picker, that picker already states the window
    // a few inches above — so the card's copy of it is redundant text taking header width away from
    // the title. Collapse it to the calendar icon; the range stays available on hover.
    iconOnlyWhenLinked: true,
    // Unlinked, the range is the card's own and worth showing -- unless spelling it out would push
    // the title onto a second line. Then it collapses the same way but stays clickable.
    iconOnly: shouldCollapseCardDatePicker({
      titleWidth,
      titleSlotWidth,
      pickerWidth,
      expandedPickerWidth
    }),
    // Same surface as the link button's tooltip immediately to its right — the two sit side by side
    // in the header, so a bordered, larger-text popover on one of them reads as a mistake.
    tooltipClassName: 'chart-tooltip'
  };

  // Seed the comparison sub-picker from any override, else the chart's saved comparison window (q1).
  const comparisonRange = isComparison
    ? toPickerRange(resolveComparisonRange(chartId) ?? config?.queries[1]?.dateRange)
    : undefined;

  return (
    <span ref={pickerRef} className='dashboard-card-date-picker'>
      {isComparison ? (
        <DatePicker
          {...commonProps}
          comparison
          comparisonRange={comparisonRange}
          comparisonOnChange={(r: DateRange) =>
            setPerChartComparisonRange(chartId, toChartDateRange(r))
          }
        />
      ) : (
        <DatePicker {...commonProps} />
      )}
      {/* The expanded button, drawn invisibly so its width is known even while the real one is
          collapsed -- the counterfactual shouldCollapseCardDatePicker needs. */}
      <span className='dashboard-card-measure' aria-hidden>
        <span ref={expandedPickerRef}>
          <DatePickerButton
            range={commonProps.range}
            comparison={isComparison}
            comparisonRange={comparisonRange}
            design={commonProps.design}
            size={commonProps.size}
          />
        </span>
      </span>
    </span>
  );
};

interface DashboardCardTitleProps {
  // Receives an invisible one-line copy of the title, whose width is what the title would need to
  // fit on a single line. Read by the card's date picker.
  naturalWidthRef: (el: HTMLSpanElement | null) => void;
  isEditMode: boolean;
  // The chart's actually-saved title (independent of any pending working copy) — the fallback handleChange
  // resolves to when the field is cleared or typed back to it, so the working copy never sits on a
  // stale, already-superseded value the visible field no longer shows.
  savedTitle?: string;
  // Commits an edited title as a new working copy — the SAME mechanism the agent uses for a proposed
  // chart edit. A title is a chart-level property (a chart can be on more than one dashboard), so it
  // goes through the same Update/Save-as-new/Discard exits as any other chart edit rather than saving
  // straight through.
  setWorkingCopy: (config: ChartConfig) => void;
}

// The chart title, rendered in the card header (left). Reads it through the card's shared render
// query (useThisCardChartData, including previewConfig), so React Query dedupes to a single fetch
// (no extra network) and the header reflects a pending edit's title. No title shows while loading.
// In edit mode it becomes a text input — matching how the dashboard's own name edits in
// DashboardHeader — that commits into the working copy on blur/Enter.
// Longer than the 250ms most search/filter debounces in the app use: committing a title schedules a
// real /getData fetch (via useChartData's config-keyed query), not a local recompute, so it's worth
// giving a fast typist more room before that cost fires per keystroke.
const TITLE_COMMIT_DEBOUNCE_MS = 400;

const DashboardCardTitle = ({
  naturalWidthRef,
  isEditMode,
  savedTitle,
  setWorkingCopy
}: DashboardCardTitleProps) => {
  const { data } = useThisCardChartData();
  const title = data?.config.title ?? '';
  // VERTICAL: the title wraps up to 2 lines (line-clamp in DashboardCard.scss), not a single
  // ellipsis-truncated line, so truncation shows up as clipped height, not clipped width.
  const [titleRef, isTruncated] = useIsTruncated<HTMLSpanElement>(title, TruncationAxis.VERTICAL);
  const [hovered, setHovered] = useState(false);
  // Re-seeds when the underlying title changes (a Discard, or the commit below resolving to the new
  // title) so the field never shows stale text.
  const [draftTitle, setDraftTitle] = useState(title);
  useEffect(() => setDraftTitle(title), [title]);

  // Debounces the title's working-copy commit so a fast typist doesn't fire a fresh /getData request
  // per character — every keystroke otherwise changes useChartData's config-hashed query key. Declared
  // ahead of the `if (!data) return null` below (Rules of Hooks); `data`/`savedTitle`/`title` are read
  // from the closure at the time the timer actually fires, not when it was scheduled.
  const [scheduleTitleCommit] = useDebouncedCallback((trimmed: string) => {
    if (!data) return;
    setWorkingCopy({ ...data.config, title: trimmed || (savedTitle ?? title) });
  }, TITLE_COMMIT_DEBOUNCE_MS);

  if (!data) return null;

  if (isEditMode) {
    // Commits on every keystroke, not blur — DashboardCardEditControls (the Update/Save-as-new/
    // Discard exits) only appears once a working copy exists, so waiting for blur would leave those
    // exits invisible while the user is still actively typing. A cleared or reverted-to-saved field
    // resolves back to the saved title rather than being skipped: `title` here is the CURRENT effective
    // title (already reflecting any earlier keystroke's commit), so skipping on a match/empty left the
    // working copy pinned to whatever was last typed — stale and different from what the field visibly
    // shows the moment the user backs all the way out of an edit.
    const handleChange = (value: string) => {
      setDraftTitle(value);
      scheduleTitleCommit(value.trim());
    };
    return (
      <Form.Control
        value={draftTitle}
        onChange={e => handleChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
        className='dashboard-card-title-input'
      />
    );
  }

  // A manual Overlay (vs OverlayTrigger) keeps the title span from being re-parented when the tooltip
  // toggles — re-parenting remounts the node and breaks the truncation ResizeObserver. Reveal the
  // full title only when it's actually clipped, using the shared light tooltip surface.
  return (
    <>
      <span
        ref={titleRef}
        className='dashboard-card-title'
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {title}
        {/* Rendered as part of the title's own text, matching how the rest of the app places an
            info tooltip trailing a title (e.g. SingleChartView) — inline so it wraps with the
            line-clamped text instead of sitting off to the side as its own flex item. */}
        <ChartContextInfo config={data.config} inline />
      </span>
      <span className='dashboard-card-measure' aria-hidden>
        <span ref={naturalWidthRef}>
          {title}
          <ChartContextInfo config={data.config} inline />
        </span>
      </span>
      <Overlay
        target={() => titleRef.current}
        show={isTruncated && hovered}
        placement={Placement.BOTTOM_START}
      >
        {/* `chart-tooltip` (borderless, tighter, smaller text) is defined in chart-tooltip.scss,
            loaded via the ChartContextInfo rendered inside this title. */}
        <Popover className='info-tooltip-popover chart-tooltip'>
          <Popover.Body>{title}</Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};
